import { Box } from '@kluein/klue-ui';
import { WarningCircle } from '@phosphor-icons/react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useAuth } from 'contexts/auth';
import { useNotifier, NotifierType } from 'hooks/use-notifier';
import { AnalyticsCategory } from 'lib/analytics';
import { useUpdateCardMutation } from 'store/api/cards';

import useEditTags from '../../../card-editor/hooks/useEditTags';
import CardEditorTags from '../../../card-editor/partials/CardEditorTags';
import { KlueCardTags } from '../klue-card-tags';

import type { KlueCardTagEditorProps } from './KlueCardTagEditor.types';
import type { KlueCardTagDataType } from 'api/api.types';
import type { Dispatch } from 'store/store.types';

const TAG_COUNT_REFRESH_DELAY = 1000;

const KlueCardTagEditor = ({
  cardId,
  cardData,
  showTags,
  analytics,
  isCardViewerContent,
  shouldOpenInNewTab,
  rank,
}: KlueCardTagEditorProps) => {
  const { isCurator } = useAuth();
  const { editedTags, addTag, removeTag } = useEditTags(
    showTags && isCurator ? cardData?.tags : undefined,
  );
  const [updateCard] = useUpdateCardMutation();
  const { notify } = useNotifier();
  const { t: translate } = useTranslation();
  const dispatch = useDispatch<Dispatch>();
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateTagState = async (tagList: KlueCardTagDataType[]) => {
    if (!cardId) return;

    // Immediately update Redux state for filtering consistency
    const existingCard = await dispatch.cards.loadOrFetchCardById(cardId);
    if (existingCard) {
      const updatedCard = {
        ...existingCard,
        tags: tagList,
      };
      dispatch.cards.populate({ cards: { [cardId]: updatedCard } });
    }

    // Clear any existing timeout to avoid multiple pending refreshes
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // Refresh tag counts after a delay to allow search index processing
    refreshTimeoutRef.current = setTimeout(() => {
      dispatch.profileFilters.refreshTagCountsIfVisible(undefined);
      refreshTimeoutRef.current = null;
    }, TAG_COUNT_REFRESH_DELAY);
  };

  const handleAddTag = async (tag: KlueCardTagDataType) => {
    if (!cardId) return;

    try {
      const updatedTags = [...editedTags, tag];
      const tagIds = updatedTags.map((t) => t.id);
      await updateCard({
        cardIds: [cardId],
        tags: tagIds,
      }).unwrap();

      addTag(tag);

      await updateTagState(updatedTags);
    } catch (error) {
      notify({
        type: NotifierType.ERROR,
        message: translate('Card:tags.saveError'),
        icon: <WarningCircle />,
      });
    }
  };

  const handleRemoveTag = async (tag: KlueCardTagDataType) => {
    if (!cardId) return;

    try {
      const updatedTags = editedTags.filter((t) => t.id !== tag.id);
      const tagIds = updatedTags.map((t) => t.id);
      await updateCard({
        cardIds: [cardId],
        tags: tagIds,
      }).unwrap();

      removeTag(tag);

      await updateTagState(updatedTags);
    } catch (error) {
      notify({
        type: NotifierType.ERROR,
        message: translate('Card:tags.saveError'),
        icon: <WarningCircle />,
      });
    }
  };

  if (!showTags) {
    return null;
  }

  if (isCurator) {
    return (
      <Box margin={{ horizontal: 'large', bottom: 'large' }}>
        <CardEditorTags
          tags={editedTags}
          onAddTag={handleAddTag}
          onRemoveTag={handleRemoveTag}
        />
      </Box>
    );
  }

  return (
    <KlueCardTags
      tags={cardData?.tags}
      t={translate}
      analytics={{
        category: analytics.category || AnalyticsCategory.profile,
      }}
      isCardViewerContent={isCardViewerContent}
      cardId={cardId}
      shouldOpenInNewTab={shouldOpenInNewTab}
      rank={rank}
    />
  );
};

export default KlueCardTagEditor;
